import { Alert, Anchor, Badge, Button, Group, Modal, Stack, Table, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { detailKey, listKey } from '../../api/queryKeys';
import { EmptyNote } from '../../components/RecordScreen';
import { PaperOnRecord } from '../../components/ThePaperItself';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { useBaseCurrency } from '../../hooks/useSeedLocation';
import { formatDate, formatDateTime, formatMoney, formatQty } from '../../lib/format';
import { CorrectDeliveryForm } from './CorrectDeliveryForm';

const RECEIPTS_PATH = '/admin/v1/suppliers/receipts';

interface DeliveryLine {
  id: string;
  inventoryItemId: string;
  purchaseOrderLineId?: string | null;
  qty: number;
  unit: string;
  unitPriceMinor: number;
  currency: string;
  goodsValueBaseMinor: number;
  freightShareBaseMinor: number;
  landedTotalBaseMinor: number;
  landedUnitCostBaseMinor: number;
}

interface DeliveryDetail {
  receipt: {
    id: string;
    supplierId: string;
    invoiceNumber?: string | null;
    invoiceCurrency: string;
    receivedAt: string;
    status: string;
    /** The photographed delivery note. Null means nobody took one — not that it is lost. */
    sourceDocumentId?: string | null;
    /**
     * Corrections. `correctedByReceiptId` set means this is the delivery AS IT WAS TYPED and
     * something has replaced it; `correctsReceiptId` set means this is the replacement. Both are
     * handles for opening the other one, never something a person is shown.
     */
    correctsReceiptId?: string | null;
    correctedByReceiptId?: string | null;
    correctedAt?: string | null;
    correctionReason?: string | null;
  };
  supplierName: string | null;
  lines: DeliveryLine[];
  goodsTotalBaseMinor: number;
  freightTotalBaseMinor: number;
  freightCharges: {
    id: string;
    carrierName: string | null;
    reference: string | null;
    amountMinor: number;
    currency: string;
    chargedAt: string;
    status: string;
  }[];
}

/**
 * What was on a delivery, and what it really cost once the crossing was added.
 *
 * The list could say who delivered it, when, how many items and what the goods came to — and
 * nothing could say WHICH items. So "their invoice says nine cases, did we get nine?" was
 * unanswerable the moment the form closed, and the landed cost that the ferry's bill spread over
 * these goods days later was never shown against the delivery it was spread over: the figure
 * existed only inside the allocator and in the item's average cost.
 */
export function DeliveryDetailModal({ receiptId, onClose }: { receiptId: string; onClose: () => void }) {
  const items = useEntityIndex('inventoryItem');
  const base = useBaseCurrency();
  const queryClient = useQueryClient();
  const [correcting, setCorrecting] = useState(false);
  /** Opening the delivery this one replaced, or the one that replaced it, without leaving here. */
  const [alsoShowing, setAlsoShowing] = useState<string | null>(null);

  const delivery = useQuery({
    queryKey: detailKey(RECEIPTS_PATH, receiptId),
    queryFn: () => rawApi<DeliveryDetail>('GET', `${RECEIPTS_PATH}/${receiptId}`),
    enabled: Boolean(receiptId),
  });
  const detail = delivery.data;
  const superseded = Boolean(detail?.receipt.correctedByReceiptId);

  return (
    <Modal
      opened
      onClose={onClose}
      size="xl"
      title={`Delivery from ${detail?.supplierName ?? 'this supplier'}`}
    >
      <Stack gap="md">
        {delivery.isLoading ? (
          <EmptyNote>Loading…</EmptyNote>
        ) : delivery.isError || !detail ? (
          // Never the empty state: "nothing on it" over an outage is a claim about the delivery,
          // and this is the screen somebody opens to check a supplier's invoice against.
          <EmptyNote>
            This delivery could not be loaded. Nothing has been lost — try again in a moment.
          </EmptyNote>
        ) : (
          <>
            <Group gap="md">
              {detail.receipt.status === 'draft' && (
                <Badge variant="light" color="gray">
                  Not confirmed yet
                </Badge>
              )}
              {superseded && (
                <Badge variant="light" color="orange">
                  Corrected
                </Badge>
              )}
              {detail.receipt.correctsReceiptId && (
                <Badge variant="light" color="blue">
                  A correction
                </Badge>
              )}
              <Text size="sm" c="dimmed">
                Arrived {formatDateTime(detail.receipt.receivedAt)}
                {detail.receipt.invoiceNumber ? ` · invoice ${detail.receipt.invoiceNumber}` : ''}
              </Text>
            </Group>

            {/*
              WHAT THIS ROW IS, when it is not simply a delivery.
              A superseded one still shows the counts that were typed — that is the whole reason it
              is kept — so without this the reader is looking at figures that agree with nothing on
              any other screen and has no way to know why.
            */}
            {superseded && (
              <Alert color="orange" variant="light" title="This is what was written down at the door">
                <Stack gap={4}>
                  <Text size="sm">
                    It has been corrected, so the counts and costs below are the ones that were typed, not the
                    ones the shelf, the bill and the order now use. It is kept exactly as it was.
                  </Text>
                  {detail.receipt.correctionReason && (
                    <Text size="sm" fw={500}>
                      “{detail.receipt.correctionReason}”
                      {detail.receipt.correctedAt
                        ? ` — recorded ${formatDateTime(detail.receipt.correctedAt)}`
                        : ''}
                    </Text>
                  )}
                  <Anchor
                    component="button"
                    type="button"
                    size="sm"
                    ta="left"
                    onClick={() => setAlsoShowing(detail.receipt.correctedByReceiptId ?? null)}
                  >
                    Open the delivery that replaced it
                  </Anchor>
                </Stack>
              </Alert>
            )}
            {detail.receipt.correctsReceiptId && (
              <Text size="sm" c="dimmed">
                This replaced an earlier delivery that was written down wrong.{' '}
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  onClick={() => setAlsoShowing(detail.receipt.correctsReceiptId ?? null)}
                >
                  See what was typed at the door
                </Anchor>
              </Text>
            )}

            {detail.lines.length === 0 ? (
              <EmptyNote>Nothing was written down against this delivery.</EmptyNote>
            ) : (
              <Table.ScrollContainer minWidth={620}>
                <Table verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Item</Table.Th>
                      <Table.Th ta="right">How many</Table.Th>
                      <Table.Th ta="right">Their price</Table.Th>
                      <Table.Th ta="right">Goods came to</Table.Th>
                      <Table.Th ta="right">Crossing added</Table.Th>
                      <Table.Th ta="right">Really cost, a unit</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {detail.lines.map((line) => (
                      <Table.Tr key={line.id}>
                        <Table.Td>{items.label(line.inventoryItemId)}</Table.Td>
                        <Table.Td ta="right">{formatQty(line.qty, line.unit)}</Table.Td>
                        <Table.Td ta="right">
                          {formatMoney(line.unitPriceMinor, line.currency)}
                          {line.unit ? ` a ${line.unit}` : ''}
                        </Table.Td>
                        {/* From here on it is the house currency: this bar is billed in lempiras
                            and dollars, and landed cost is only comparable in one of them. */}
                        <Table.Td ta="right">{formatMoney(line.goodsValueBaseMinor, base)}</Table.Td>
                        <Table.Td ta="right">
                          {line.freightShareBaseMinor > 0
                            ? formatMoney(line.freightShareBaseMinor, base)
                            : '—'}
                        </Table.Td>
                        <Table.Td ta="right" fw={600}>
                          {formatMoney(line.landedUnitCostBaseMinor, base)}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}

            <Group justify="flex-end" gap="xl">
              <Text size="sm">
                Goods <strong>{formatMoney(detail.goodsTotalBaseMinor, base)}</strong>
              </Text>
              <Text size="sm">
                Getting it here <strong>{formatMoney(detail.freightTotalBaseMinor, base)}</strong>
              </Text>
              <Text size="sm">
                Landed{' '}
                <strong>
                  {formatMoney(detail.goodsTotalBaseMinor + detail.freightTotalBaseMinor, base)}
                </strong>
              </Text>
            </Group>

            {/* Beside the figures it is evidence for. "Their invoice says nine cases, did we get
                nine?" is answered by the count on this screen and by the paper the driver left,
                and the paper is the half that used to have nowhere to be. */}
            <PaperOnRecord
              documentId={detail.receipt.sourceDocumentId}
              attachPath={`${RECEIPTS_PATH}/${receiptId}/document`}
              subject="this delivery"
              kind="photo"
              emptyNote="No photograph of the delivery note."
              invalidate={[detailKey(RECEIPTS_PATH, receiptId), listKey(RECEIPTS_PATH)]}
            />

            <div>
              <Text size="sm" fw={500}>
                What it cost to bring over
              </Text>
              {detail.freightCharges.length === 0 ? (
                <Text size="xs" c="dimmed" mt={4}>
                  No crossing has been billed against this delivery. If a carrier's bill is still to come,
                  record it under Shipping over and these costs come up to date on their own.
                </Text>
              ) : (
                <Stack gap={2} mt={4}>
                  {detail.freightCharges.map((charge) => (
                    <Text key={charge.id} size="sm" c="dimmed">
                      {charge.carrierName ?? 'A carrier'} · {formatDate(charge.chargedAt)} ·{' '}
                      {formatMoney(charge.amountMinor, charge.currency)}
                      {charge.reference ? ` · ${charge.reference}` : ''}
                      {/* The bill may cover other deliveries too, so its total is not this
                          delivery's share — which is the figure above, and only that one. */}
                    </Text>
                  ))}
                  <Text size="xs" c="dimmed">
                    A crossing can cover more than one delivery. Only this delivery's share of it is in the
                    costs above.
                  </Text>
                </Stack>
              )}
            </div>

            <Group justify="space-between">
              {/*
                The way back from a wrong count, on the screen where a wrong count is noticed.
                Never on a superseded delivery: correcting the record of what was typed would fork
                the chain, and the server refuses it — a button that always fails is worse than no
                button, so the one that replaced it is where the correction goes.
              */}
              {superseded ? (
                <Text size="sm" c="dimmed">
                  Corrections go on the delivery that replaced this one.
                </Text>
              ) : (
                <Button variant="light" onClick={() => setCorrecting(true)}>
                  Correct this delivery
                </Button>
              )}
              <Button variant="default" onClick={onClose}>
                Close
              </Button>
            </Group>
          </>
        )}
      </Stack>

      {correcting && detail && (
        <CorrectDeliveryForm
          delivery={{
            id: detail.receipt.id,
            supplierId: detail.receipt.supplierId,
            supplierName: detail.supplierName,
            invoiceNumber: detail.receipt.invoiceNumber,
            invoiceCurrency: detail.receipt.invoiceCurrency,
            lines: detail.lines,
          }}
          onClose={() => setCorrecting(false)}
          onSaved={() => {
            // This modal is now looking at a superseded delivery, so it has to re-read rather than
            // keep showing figures nothing else agrees with.
            void queryClient.invalidateQueries({ queryKey: detailKey(RECEIPTS_PATH, receiptId) });
            onClose();
          }}
        />
      )}
      {alsoShowing && <DeliveryDetailModal receiptId={alsoShowing} onClose={() => setAlsoShowing(null)} />}
    </Modal>
  );
}
